import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Badge from '../ui/Badge';
import LoadingSpinner from '../common/LoadingSpinner';
import { isWhiteLabel } from '../../config/tenantConfig';
import { useToast } from '../common/ToastContainer';
import ConfirmDialog from '../common/ConfirmDialog';
import BulkDeleteConfirmationDialog from '../common/BulkDeleteConfirmationDialog';
import {
  Plus, Search,
  Trash2, Globe, ArrowLeft, Edit3, HelpCircle, Upload, Copy,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, GripVertical,
  ExternalLink
} from 'lucide-react';
import { Basket, StrategyMetadata, StrategyWithLegs, CreateBasket, UpdateBasket } from '../../types';
import basketService from '../../services/basketService';
import strategyService from '../../services/strategyService';
import marketplaceService from '../../services/marketplaceService';
import { BASKET_STATUS } from '../../constants/tradingConstants';
import Tooltip from '../ui/Tooltip';
import Dropdown from '../ui/Dropdown';
import EnableToggle from '../ui/EnableToggle';
import BasketPerformanceTab from './BasketPerformanceTab';
import CreateBasketDialog from './CreateBasketDialog';
import EditBasketDialog from './EditBasketDialog';
import StrategyEditorModal from '../strategy/StrategyEditorModal';
import StrategyRowView from '../strategy/StrategyRowView';
import BasketAllocation from './BasketAllocation';
import BasketScheduleView from './BasketScheduleView';
import PublishBasketDialog from '../marketplace/PublishBasketDialog';
import ExportStrategiesDialog from './ExportStrategiesDialog';
import CopyStrategiesDialog from './CopyStrategiesDialog';
import { useAuth } from '../../context/AuthContext';
import { readPersistedValue, writePersistedValue } from '../../utils/persistedSelect';

interface BasketWithStrategies extends Omit<Basket, 'strategies'> {
  strategies?: StrategyMetadata[]; // Make strategies optional initially
  totalPnL: number;
  lastExecution?: string;
  is_published?: boolean;
}

const TabbedBasketManager: React.FC = () => {
  const wl = isWhiteLabel();
  const { showSuccess, showError } = useToast();
  const { isAdmin } = useAuth() as any;
  const [publishingBasket, setPublishingBasket] = useState<BasketWithStrategies | null>(null);
  const [exportingBasket, setExportingBasket] = useState<BasketWithStrategies | null>(null);
  const [copyingFromBasket, setCopyingFromBasket] = useState<BasketWithStrategies | null>(null);
  const [baskets, setBaskets] = useState<BasketWithStrategies[]>([]);
  const [selectedBasket, setSelectedBasket] = useState<BasketWithStrategies | null>(null);
  const [loading, setLoading] = useState(true);
  const [strategiesLoading, setStrategiesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  type SortByValue = 'custom' | 'name-asc' | 'name-desc' | 'newest' | 'oldest' | 'recently-modified' | 'least-recently-modified';
  type StatusFilterValue = 'all' | 'enabled' | 'disabled';
  const SORT_BY_VALUES: readonly SortByValue[] = ['custom', 'name-asc', 'name-desc', 'newest', 'oldest', 'recently-modified', 'least-recently-modified'];
  const STATUS_FILTER_VALUES: readonly StatusFilterValue[] = ['all', 'enabled', 'disabled'];
  const [sortBy, setSortBy] = useState<SortByValue>(() =>
    readPersistedValue('basketSortBy', SORT_BY_VALUES, 'custom'),
  );
  // Index of the row currently being dragged (basket reorder)
  const [draggedBasketIndex, setDraggedBasketIndex] = useState<number | null>(null);
  const [dragOverBasketIndex, setDragOverBasketIndex] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>(() =>
    readPersistedValue('basketStatusFilter', STATUS_FILTER_VALUES, 'all'),
  );

  const PANEL_MIN_WIDTH = 200;
  const PANEL_MAX_WIDTH = 600;
  const PANEL_DEFAULT_WIDTH = 280;
  const PANEL_COLLAPSED_WIDTH = 44;
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return PANEL_DEFAULT_WIDTH;
    const stored = parseInt(localStorage.getItem('basketPanelWidth') || '', 10);
    return Number.isFinite(stored)
      ? Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, stored))
      : PANEL_DEFAULT_WIDTH;
  });
  const [panelCollapsed, setPanelCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('basketPanelCollapsed') === '1';
  });
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  const resizeStartRef = useRef<{ x: number; w: number } | null>(null);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditBasketDialog, setShowEditBasketDialog] = useState(false);
  const [showStrategyWizard, setShowStrategyWizard] = useState(false);
  const [showEditStrategyDialog, setShowEditStrategyDialog] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState<StrategyWithLegs | null>(null);

  type BasketTab = 'details' | 'performance' | 'allocation' | 'live';
  const BASKET_TAB_VALUES: readonly BasketTab[] = ['details', 'performance', 'allocation', 'live'];
  const [activeTab, setActiveTab] = useState<BasketTab>(() =>
    readPersistedValue('basketActiveTab', BASKET_TAB_VALUES, 'details'),
  );
  useEffect(() => {
    writePersistedValue('basketActiveTab', activeTab);
  }, [activeTab]);
  // Shared active-tab pill classes — whitelabel-aware (blue → zebu).
  const activeTabCls = wl
    ? 'bg-zebu-100 dark:bg-zebu-900/30 text-zebu-700 dark:text-zebu-300'
    : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
  const idleTabCls = 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white';
  const [updatingBasket, setUpdatingBasket] = useState<string | null>(null);
  const [updatingStrategy, setUpdatingStrategy] = useState<string | null>(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    isOpen: boolean;
    basket: BasketWithStrategies | null;
  }>({ isOpen: false, basket: null });

  const [deleteStrategyConfirmDialog, setDeleteStrategyConfirmDialog] = useState<{
    isOpen: boolean;
    strategy: StrategyMetadata | null;
  }>({ isOpen: false, strategy: null });

  const [bulkDeleteDialog, setBulkDeleteDialog] = useState<{
    isOpen: boolean;
    basket: BasketWithStrategies | null;
    isDeleting: boolean;
  }>({ isOpen: false, basket: null, isDeleting: false });

  // Load baskets on component mount
  useEffect(() => {
    loadBaskets();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track desktop breakpoint (only desktop supports collapse + resize)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Persist panel width and collapsed state
  useEffect(() => {
    try { localStorage.setItem('basketPanelWidth', String(panelWidth)); } catch {}
  }, [panelWidth]);
  useEffect(() => {
    try { localStorage.setItem('basketPanelCollapsed', panelCollapsed ? '1' : '0'); } catch {}
  }, [panelCollapsed]);

  // Persist sort + filter selections so they survive navigation away/back
  useEffect(() => { writePersistedValue('basketSortBy', sortBy); }, [sortBy]);
  useEffect(() => { writePersistedValue('basketStatusFilter', statusFilter); }, [statusFilter]);

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    resizeStartRef.current = { x: e.clientX, w: panelWidth };
    const onMove = (ev: MouseEvent) => {
      const start = resizeStartRef.current;
      if (!start) return;
      const next = Math.max(
        PANEL_MIN_WIDTH,
        Math.min(PANEL_MAX_WIDTH, start.w + (ev.clientX - start.x))
      );
      setPanelWidth(next);
    };
    const onUp = () => {
      resizeStartRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // ------------------------------------------------------------------------
  // Custom basket ordering (drag-and-drop on desktop, arrow buttons on mobile)
  // ------------------------------------------------------------------------
  // Reorder is allowed only when sortBy === 'custom'. Apply optimistically to
  // local state, then call the backend; on failure, revert with a toast.
  const applyBasketReorder = async (orderedIds: string[]) => {
    const previousBaskets = baskets;
    const byId = new Map(previousBaskets.map(b => [b.basket_id, b] as const));
    const reordered: BasketWithStrategies[] = [];
    orderedIds.forEach((id, idx) => {
      const basket = byId.get(id);
      if (basket) reordered.push({ ...basket, display_order: idx });
    });
    if (reordered.length !== previousBaskets.length) return; // safety net
    setBaskets(reordered);
    try {
      await basketService.reorderBaskets(orderedIds);
    } catch (error: any) {
      setBaskets(previousBaskets);
      showError(error.message || 'Failed to save basket order');
    }
  };

  const handleBasketDragStart = (e: React.DragEvent, index: number) => {
    setDraggedBasketIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Required by Firefox to start a native drag
    try { e.dataTransfer.setData('text/plain', String(index)); } catch {}
  };

  const handleBasketDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverBasketIndex(index);
  };

  const handleBasketDragLeave = (index: number) => {
    setDragOverBasketIndex(prev => (prev === index ? null : prev));
  };

  const handleBasketDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const from = draggedBasketIndex;
    setDraggedBasketIndex(null);
    setDragOverBasketIndex(null);
    if (from === null || from === dropIndex) return;
    const ordered = [...filteredBaskets];
    const [moved] = ordered.splice(from, 1);
    ordered.splice(dropIndex, 0, moved);
    void applyBasketReorder(ordered.map(b => b.basket_id));
  };

  const handleBasketMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= filteredBaskets.length) return;
    const ordered = [...filteredBaskets];
    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    void applyBasketReorder(ordered.map(b => b.basket_id));
  };

  // ------------------------------------------------------------------------
  // Custom strategy ordering — same pattern, scoped to the selected basket.
  // ------------------------------------------------------------------------
  const applyStrategyReorder = async (orderedIds: string[]) => {
    if (!selectedBasket) return;
    const previousSelected = selectedBasket;
    const previousBaskets = baskets;
    const currentStrategies = selectedBasket.strategies || [];
    const byId = new Map(currentStrategies.map(s => [s.strategyId, s] as const));
    const reordered: StrategyMetadata[] = [];
    orderedIds.forEach((id, idx) => {
      const strat = byId.get(id);
      if (strat) reordered.push({ ...strat, displayOrder: idx });
    });
    if (reordered.length !== currentStrategies.length) return;

    const updatedBasket: BasketWithStrategies = { ...previousSelected, strategies: reordered };
    setSelectedBasket(updatedBasket);
    setBaskets(prev => prev.map(b =>
      b.basket_id === updatedBasket.basket_id ? updatedBasket : b,
    ));
    try {
      await strategyService.reorderStrategies(previousSelected.basket_id, orderedIds);
    } catch (error: any) {
      setSelectedBasket(previousSelected);
      setBaskets(previousBaskets);
      showError(error.message || 'Failed to save strategy order');
    }
  };

  // Listen for external create basket events
  useEffect(() => {
    const handleCreateBasketEvent = () => {
      setShowCreateDialog(true);
    };

    window.addEventListener('openCreateBasketDialog', handleCreateBasketEvent);

    return () => {
      window.removeEventListener('openCreateBasketDialog', handleCreateBasketEvent);
    };
  }, []);

  // Load strategies when a basket is selected.
  useEffect(() => {
    let isCancelled = false;

    const loadBasketStrategies = async (basketId: string) => {
      if (isCancelled) return;

      try {
        setStrategiesLoading(true);
        const strategies = await strategyService.getBasketStrategies(basketId);
        if (isCancelled) return;

        // Update the selected basket with fetched strategies (prevent infinite loop)
        setSelectedBasket(prev => {
          if (!prev || prev.basket_id !== basketId) return prev;
          return { ...prev, strategies };
        });

        // Also update the basket in the baskets list
        setBaskets(prev =>
          prev.map(basket =>
            basket.basket_id === basketId
              ? { ...basket, strategies }
              : basket
          )
        );
      } catch (error: any) {
        if (!isCancelled) {
          const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
          console.error('Failed to load strategies:', error);
          showError(`Failed to load strategies: ${errorMessage}`);
        }
      } finally {
        if (!isCancelled) setStrategiesLoading(false);
      }
    };

    if (selectedBasket?.basket_id) {
      // Only load strategies if we don't already have them or if they're empty
      if (!selectedBasket.strategies || selectedBasket.strategies.length === 0) {
        loadBasketStrategies(selectedBasket.basket_id);
      } else {
        setStrategiesLoading(false);
      }
    } else {
      setStrategiesLoading(false);
    }

    return () => { isCancelled = true; };
    // Intentionally key on basket_id only — re-firing on every selectedBasket
    // object identity change would loop (loadBasketStrategies updates
    // strategies, which would re-trigger via the parent's selectedBasket ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBasket?.basket_id, showError]);

  const loadBaskets = async () => {
    try {
      setLoading(true);
      const basketsData = await basketService.getBaskets();

      // Fetch published basket IDs for admins to show disabled publish button
      const publishedBasketIds = new Set<string>();
      if (isAdmin) {
        try {
          const listings = await marketplaceService.getAdminListings();
          listings.forEach(l => publishedBasketIds.add(l.basket_id));
        } catch {
          // Non-critical — publish button will default to enabled
        }
      }

      // Enhance baskets with additional data (no strategy count API calls)
      const enhancedBaskets: BasketWithStrategies[] = basketsData.map(basket => ({
        ...basket,
        totalPnL: Math.random() * 2000 - 500, // Mock data
        lastExecution: Math.random() > 0.5 ? new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString() : undefined,
        is_published: publishedBasketIds.has(basket.basket_id),
      }));

      setBaskets(enhancedBaskets);

      // Auto-select first basket if available
      if (enhancedBaskets.length > 0 && !selectedBasket) {
        setSelectedBasket(enhancedBaskets[0]);
      }
    } catch (error: any) {
      console.error('Failed to load baskets:', error);
      showError('Failed to load baskets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBasketStatusToggle = async (basket: BasketWithStrategies) => {
    const newStatus = basket.status === BASKET_STATUS.ACTIVE ? BASKET_STATUS.INACTIVE : BASKET_STATUS.ACTIVE;
    try {
      setUpdatingBasket(basket.basket_id);
      await basketService.updateBasketStatus(basket.basket_id, newStatus);

      setBaskets(prev =>
        prev.map(b =>
          b.basket_id === basket.basket_id
            ? { ...b, status: newStatus }
            : b
        )
      );

      if (selectedBasket?.basket_id === basket.basket_id) {
        setSelectedBasket(prev => prev ? { ...prev, status: newStatus } : null);
      }

      showSuccess(`Basket ${newStatus.toLowerCase()} successfully`);
    } catch (error: any) {
      showError(error.message || 'Failed to update basket status');
    } finally {
      setUpdatingBasket(null);
    }
  };

  const handleCreateBasket = async (basketData: CreateBasket) => {
    try {
      const createdBasket = await basketService.createBasket(basketData);
      
      const newBasket: BasketWithStrategies = {
        ...createdBasket,
        totalPnL: 0,
      };
      
      setBaskets(prev => [newBasket, ...prev]);
      setSelectedBasket(newBasket);
      setShowCreateDialog(false);
      showSuccess(`Basket "${createdBasket.basket_name || 'Unnamed'}" created successfully!`);
      
    } catch (error: any) {
      console.error('Failed to create basket:', error);
      // Don't close the dialog on error, let the dialog handle the error display
      throw error; // Re-throw to let the dialog handle the error
    }
  };

  const handleEditBasket = async (basketId: string, updates: UpdateBasket) => {
    try {
      const updated = await basketService.updateBasket(basketId, updates);
      setBaskets(prev => prev.map(b => b.basket_id === basketId ? { ...b, ...updated, strategies: b.strategies } : b));
      setSelectedBasket(prev => prev && prev.basket_id === basketId ? { ...prev, ...updated, strategies: prev.strategies } : prev);
      setShowEditBasketDialog(false);
      showSuccess('Basket updated successfully!');
    } catch (error: any) {
      throw error;
    }
  };

  const handleDeleteBasket = (basket: BasketWithStrategies) => {
    // Show modern confirmation dialog
    setDeleteConfirmDialog({
      isOpen: true,
      basket: basket
    });
  };

  const confirmDeleteBasket = async () => {
    if (!deleteConfirmDialog.basket) return;

    try {
      await basketService.deleteBasket(deleteConfirmDialog.basket.basket_id);

      // Remove from local state
      setBaskets(prev => prev.filter(b => b.basket_id !== deleteConfirmDialog.basket!.basket_id));

      // Clear selection if the deleted basket was selected
      if (selectedBasket?.basket_id === deleteConfirmDialog.basket.basket_id) {
        setSelectedBasket(null);
      }

      showSuccess(`Basket "${deleteConfirmDialog.basket.basket_name}" deleted successfully!`);

      // Close dialog
      setDeleteConfirmDialog({ isOpen: false, basket: null });

    } catch (error: any) {
      console.error('Failed to delete basket:', error);
      showError(error.message || 'Failed to delete basket');
    }
  };

  const handleAddStrategy = async (createResult: any) => {
    try {
      if (!selectedBasket || !createResult.success) {
        if (!createResult.success) {
          throw new Error(createResult.message || 'Failed to create strategy');
        }
        return;
      }

      // Extract the real strategy data from API response
      const realStrategyData = createResult.createdStrategy;

      if (!realStrategyData) {
        throw new Error('No strategy data received from API');
      }

      // Transform the backend strategy to frontend format using our existing utility
      const { transformStrategyFields } = await import('../../utils/transformStrategyFields');
      const newStrategy = transformStrategyFields(realStrategyData);

      const updatedBasket = {
        ...selectedBasket,
        strategies: [...(selectedBasket.strategies || []), newStrategy]
      };

      setBaskets(prev =>
        prev.map(basket =>
          basket.basket_id === selectedBasket.basket_id ? updatedBasket : basket
        )
      );

      setSelectedBasket(updatedBasket);
      setShowStrategyWizard(false);
    } catch (error: any) {
      console.error('Failed to add strategy:', error);
      showError(error.message || 'Failed to add strategy. Please try again.');
    }
  };

  const handleEditStrategy = async (strategy: StrategyMetadata) => {
    try {
      setEditingStrategy(null);

      const strategyId = strategy.strategyId;
      if (!strategyId) {
        throw new Error('Strategy ID not found. Available properties: ' + Object.keys(strategy).join(', '));
      }

      // Fetch full strategy details from API and open the editor.
      const fullStrategyData = await strategyService.getStrategy(strategyId);
      setEditingStrategy(fullStrategyData);
      setShowEditStrategyDialog(true);
    } catch (error: any) {
      console.error('Failed to fetch strategy for editing:', error);
      showError(error.message || 'Failed to load strategy for editing. Please try again.');
    }
  };

  const handleUpdateStrategy = async (updateResult: any) => {
    try {
      if (!editingStrategy || !updateResult.success) {
        if (!updateResult.success) {
          throw new Error(updateResult.message || 'Failed to update strategy');
        }
        return;
      }

      // Instead of local state merging, refresh complete strategy list from API
      // This ensures we get fresh data including updated TP/SL values
      if (selectedBasket) {
        try {
          const strategies = await strategyService.getBasketStrategies(selectedBasket.basket_id);

          // Update selected basket with fresh strategies
          setSelectedBasket(prev => prev ? { ...prev, strategies } : null);

          // Update baskets list with fresh strategies
          setBaskets(prev =>
            prev.map(basket =>
              basket.basket_id === selectedBasket.basket_id
                ? { ...basket, strategies }
                : basket
            )
          );
        } catch (refreshError) {
          // Don't throw — the update was successful, just the refresh failed.
          console.error('Failed to refresh strategies after update:', refreshError);
        }
      }

      // Clean up edit state
      setShowEditStrategyDialog(false);
      setEditingStrategy(null);
    } catch (error: any) {
      console.error('Failed to update strategy:', error);
      showError(error.message || 'Failed to update strategy. Please try again.');
    }
  };

  const handleEntrySelectionUpdate = async (
    strategyId: string,
    payload: { entrySelectionMode: 'WEEKDAY' | 'DTE'; entryDays: string[]; entryDtes: number[] },
  ) => {
    try {
      setUpdatingStrategy(strategyId);
      await strategyService.updateStrategy(strategyId, {
        entry_selection_mode: payload.entrySelectionMode,
        entry_days: payload.entryDays,
        entry_dtes: payload.entryDtes,
      } as any);

      // Update local state with the full payload so the row re-renders with
      // both the active mode and the preserved inactive selection.
      const updateStrategies = (list: StrategyMetadata[] | undefined) =>
        list?.map(s => s.strategyId === strategyId
          ? {
              ...s,
              entrySelectionMode: payload.entrySelectionMode,
              entryDays: payload.entryDays,
              entryDtes: payload.entryDtes,
            }
          : s);

      setSelectedBasket(prev => prev ? { ...prev, strategies: updateStrategies(prev.strategies) } : null);
      setBaskets(prev => prev.map(b =>
        b.basket_id === selectedBasket?.basket_id
          ? { ...b, strategies: updateStrategies(b.strategies) }
          : b
      ));
    } catch (error: any) {
      showError(error.message || 'Failed to update entry selection');
      throw error;
    } finally {
      setUpdatingStrategy(null);
    }
  };

  const handleStatusToggle = async (strategy: StrategyMetadata) => {
    const newStatus = strategy.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      setUpdatingStrategy(strategy.strategyId);
      await strategyService.updateStrategyStatus(strategy.strategyId, newStatus);
      showSuccess(`Strategy "${strategy.strategyName}" ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'}`);

      if (selectedBasket) {
        const strategies = await strategyService.getBasketStrategies(selectedBasket.basket_id);
        setSelectedBasket(prev => prev ? { ...prev, strategies } : null);
        setBaskets(prev => prev.map(basket =>
          basket.basket_id === selectedBasket.basket_id ? { ...basket, strategies } : basket
        ));
      }
    } catch (e: any) {
      showError(e?.message || 'Failed to update strategy status');
    } finally {
      setUpdatingStrategy(null);
    }
  };

  const handleDeleteStrategy = (strategy: StrategyMetadata) => {
    // Show confirmation dialog
    setDeleteStrategyConfirmDialog({
      isOpen: true,
      strategy: strategy
    });
  };

  const handleCopyStrategy = async (strategy: StrategyMetadata) => {
    if (!selectedBasket) return;
    setUpdatingStrategy(strategy.strategyId);
    try {
      await strategyService.cloneStrategy(strategy.strategyId, selectedBasket.basket_id);
      showSuccess(`Strategy "${strategy.strategyName}" duplicated`);
      const strategies = await strategyService.getBasketStrategies(selectedBasket.basket_id);
      setSelectedBasket(prev => prev ? { ...prev, strategies } : null);
      setBaskets(prev => prev.map(b =>
        b.basket_id === selectedBasket.basket_id ? { ...b, strategies } : b
      ));
    } catch (e: any) {
      showError(e?.message || 'Failed to duplicate strategy');
    } finally {
      setUpdatingStrategy(null);
    }
  };

  const confirmDeleteStrategy = async () => {
    if (!deleteStrategyConfirmDialog.strategy) return;

    try {
      // Delete the strategy
      await strategyService.deleteStrategy(deleteStrategyConfirmDialog.strategy.strategyId);

      showSuccess(`Strategy "${deleteStrategyConfirmDialog.strategy.strategyName}" deleted successfully`);

      // Refresh the strategy list
      if (selectedBasket) {
        const strategies = await strategyService.getBasketStrategies(selectedBasket.basket_id);

        // Update selected basket with refreshed strategies
        setSelectedBasket(prev => prev ? {
          ...prev,
          strategies: strategies
        } : null);

        // Update baskets list
        setBaskets(prev => prev.map(basket =>
          basket.basket_id === selectedBasket.basket_id
            ? { ...basket, strategies: strategies }
            : basket
        ));
      }

      // Close the confirmation dialog
      setDeleteStrategyConfirmDialog({ isOpen: false, strategy: null });
    } catch (error) {
      console.error('Error deleting strategy:', error);
      showError(error instanceof Error ? error.message : 'Failed to delete strategy');
    }
  };

  // Bulk delete strategies handlers
  const handleBulkDeleteStrategies = (basket: BasketWithStrategies) => {
    if (!basket.strategies || basket.strategies.length === 0) {
      showError('No strategies to delete in this basket');
      return;
    }

    setBulkDeleteDialog({
      isOpen: true,
      basket: basket,
      isDeleting: false
    });
  };

  const confirmBulkDeleteStrategies = async () => {
    if (!bulkDeleteDialog.basket) return;

    try {
      // Set deleting state
      setBulkDeleteDialog(prev => ({ ...prev, isDeleting: true }));

      const basket = bulkDeleteDialog.basket;

      // Call the bulk delete API
      const result = await strategyService.deleteAllBasketStrategies(basket.basket_id);

      // Close the dialog
      setBulkDeleteDialog({ isOpen: false, basket: null, isDeleting: false });

      // Show appropriate success message
      if (result.failedCount > 0) {
        showSuccess(
          `Bulk deletion completed: ${result.deletedCount} strategies deleted successfully. ${result.failedCount} failures.`
        );
        console.warn('Some strategies failed to delete:', result.failedStrategyIds);
      } else {
        showSuccess(`Successfully deleted all ${result.deletedCount} strategies from basket "${basket.basket_name}"`);
      }

      // Refresh the strategy list for the current basket
      if (selectedBasket && selectedBasket.basket_id === basket.basket_id) {
        try {
          const strategies = await strategyService.getBasketStrategies(selectedBasket.basket_id);

          // Update selected basket with refreshed strategies
          setSelectedBasket(prev => prev ? {
            ...prev,
            strategies: strategies
          } : null);
        } catch (refreshError) {
          console.warn('Failed to refresh strategies after bulk delete:', refreshError);
          // Clear strategies from UI since they should be deleted
          setSelectedBasket(prev => prev ? {
            ...prev,
            strategies: []
          } : null);
        }
      }

      // Update baskets list to reflect empty strategies
      setBaskets(prev => prev.map(b =>
        b.basket_id === basket.basket_id
          ? { ...b, strategies: [] }
          : b
      ));

    } catch (error: any) {
      console.error('Error during bulk delete:', error);
      showError(error.message || 'Failed to delete all strategies');

      // Reset deleting state on error
      setBulkDeleteDialog(prev => ({ ...prev, isDeleting: false }));
    }
  };

  const filteredBaskets = baskets
    .filter(basket => {
      const matchesSearch =
        (basket.basket_name && basket.basket_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (basket.description && basket.description.toLowerCase().includes(searchQuery.toLowerCase()));
      if (!matchesSearch) return false;
      if (statusFilter === 'enabled') return basket.status === BASKET_STATUS.ACTIVE;
      if (statusFilter === 'disabled') return basket.status !== BASKET_STATUS.ACTIVE;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return (a.basket_name ?? '').localeCompare(b.basket_name ?? '');
        case 'name-desc':
          return (b.basket_name ?? '').localeCompare(a.basket_name ?? '');
        case 'newest':
          return (b.created_at ?? '').localeCompare(a.created_at ?? '');
        case 'oldest':
          return (a.created_at ?? '').localeCompare(b.created_at ?? '');
        case 'recently-modified': {
          // Fall back to created_at for legacy rows missing updated_at.
          const am = a.updated_at ?? a.created_at ?? '';
          const bm = b.updated_at ?? b.created_at ?? '';
          return bm.localeCompare(am);
        }
        case 'least-recently-modified': {
          const am = a.updated_at ?? a.created_at ?? '';
          const bm = b.updated_at ?? b.created_at ?? '';
          return am.localeCompare(bm);
        }
        case 'custom':
        default: {
          // (display_order, created_at) — legacy rows sink to the bottom.
          const ao = a.display_order ?? Number.POSITIVE_INFINITY;
          const bo = b.display_order ?? Number.POSITIVE_INFINITY;
          if (ao !== bo) return ao - bo;
          return (a.created_at ?? '').localeCompare(b.created_at ?? '');
        }
      }
    });


  return (
    <>
      {/* Mobile-First Responsive Layout */}
      <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-300px)] lg:min-h-[600px] bg-white dark:bg-gray-900 rounded-lg shadow lg:overflow-hidden">

        {/* Basket List Panel - Mobile: hidden when basket selected, Desktop: resizable + collapsible sidebar */}
        <div
          className={`w-full relative border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col ${selectedBasket ? 'hidden lg:flex' : 'flex'}`}
          style={isDesktop ? { width: panelCollapsed ? PANEL_COLLAPSED_WIDTH : panelWidth, flexShrink: 0 } : undefined}
        >
          {isDesktop && panelCollapsed ? (
            /* Collapsed strip — click to expand */
            <div className="flex flex-col items-center pt-3 gap-3 h-full">
              <button
                onClick={() => setPanelCollapsed(false)}
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Expand basket panel"
                aria-label="Expand basket panel"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <div
                className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-2 select-none"
                style={{ writingMode: 'vertical-rl' }}
              >
                Baskets ({baskets.length})
              </div>
            </div>
          ) : (
          <>
          {/* Basket List Header - Minimalist 2025 Design */}
          <div className="p-3 lg:p-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium text-gray-900 dark:text-white">Baskets</h2>
              <div className="flex items-center gap-1.5">
                <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full">
                  {baskets.length}
                </span>
                {isDesktop && (
                  <button
                    onClick={() => setPanelCollapsed(true)}
                    className="p-1 rounded-md text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Collapse panel"
                    aria-label="Collapse basket panel"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Search - Mobile optimized */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search baskets..."
                className="pl-10 text-sm h-9 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              />
            </div>

            {/* Sort & Filter - Minimalist (custom Dropdown) */}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Dropdown
                value={sortBy}
                onChange={(v) => setSortBy(v as SortByValue)}
                ariaLabel="Sort baskets"
                size="sm"
                options={[
                  { value: 'custom', label: 'Custom order' },
                  { value: 'name-asc', label: 'Name A → Z' },
                  { value: 'name-desc', label: 'Name Z → A' },
                  { value: 'newest', label: 'Newest first' },
                  { value: 'oldest', label: 'Oldest first' },
                  { value: 'recently-modified', label: 'Recently modified' },
                  { value: 'least-recently-modified', label: 'Least recently modified' },
                ]}
              />
              <Dropdown
                value={statusFilter}
                onChange={(v) => setStatusFilter(v as StatusFilterValue)}
                ariaLabel="Filter baskets by status"
                size="sm"
                options={[
                  { value: 'all', label: 'All baskets' },
                  { value: 'enabled', label: 'Enabled only' },
                  { value: 'disabled', label: 'Disabled only' },
                ]}
              />
            </div>

            {/* Create Basket Button - Minimalist */}
            <Button
              onClick={() => setShowCreateDialog(true)}
              className={`w-full mt-3 h-9 text-sm ${wl ? 'bg-zebu-600 hover:bg-zebu-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Basket
            </Button>
          </div>

          {/* Basket List */}
          <div className="flex-1 lg:overflow-y-auto">
            {loading ? (
              <div className="py-4">
                <LoadingSpinner message="Loading baskets..." />
              </div>
            ) : filteredBaskets.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-500 mb-4">Performance</div>
                  <h3 className="text-lg font-medium mb-2">No Baskets Yet</h3>
                  <p className="text-sm text-gray-600 mb-4">Create your first basket</p>
                  <Button onClick={() => setShowCreateDialog(true)} leftIcon={<Plus className="h-4 w-4" />} size="sm">
                    Create Basket
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-1 p-3">
                {filteredBaskets.map((basket, index) => {
                  const reorderEnabled = sortBy === 'custom';
                  const isDragging = draggedBasketIndex === index;
                  const isDropTarget = dragOverBasketIndex === index && draggedBasketIndex !== null && draggedBasketIndex !== index;
                  return (
                  <div
                    key={basket.basket_id}
                    draggable={reorderEnabled && isDesktop}
                    onDragStart={reorderEnabled && isDesktop ? (e) => handleBasketDragStart(e, index) : undefined}
                    onDragOver={reorderEnabled && isDesktop ? (e) => handleBasketDragOver(e, index) : undefined}
                    onDragLeave={reorderEnabled && isDesktop ? () => handleBasketDragLeave(index) : undefined}
                    onDrop={reorderEnabled && isDesktop ? (e) => handleBasketDrop(e, index) : undefined}
                    onDragEnd={reorderEnabled && isDesktop ? () => { setDraggedBasketIndex(null); setDragOverBasketIndex(null); } : undefined}
                    className={`group w-full p-3 text-left transition-colors cursor-pointer rounded-lg border ${
                      selectedBasket?.basket_id === basket.basket_id
                        ? `${wl ? 'bg-zebu-50 dark:bg-zebu-900/20 border-zebu-200 dark:border-zebu-700' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'} shadow-sm`
                        : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:border-gray-200 dark:hover:border-gray-700'
                    } ${isDragging ? 'opacity-50' : ''} ${isDropTarget ? 'border-t-2 border-t-indigo-500 dark:border-t-indigo-400' : ''}`}
                    onClick={() => setSelectedBasket(basket)}
                  >
                    {/* Row 1: drag handle (desktop) + name + status dot + actions */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {reorderEnabled && isDesktop && (
                          <span
                            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-grab active:cursor-grabbing shrink-0"
                            title="Drag to reorder"
                            aria-label="Drag handle"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <GripVertical className="h-4 w-4" />
                          </span>
                        )}
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {basket.basket_name || 'Unnamed Basket'}
                        </span>
                        {basket.stockmock_url && (
                          // Owner-only StockMock reference link. Never shown
                          // to marketplace subscribers (they never receive
                          // the field from any consumer-facing endpoint).
                          <a
                            href={basket.stockmock_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors shrink-0"
                            title="Open on StockMock"
                            aria-label="Open on StockMock"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {reorderEnabled && !isDesktop && (
                          <div className="flex items-center gap-0.5">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleBasketMove(index, -1); }}
                              disabled={index === 0}
                              className="p-1 rounded-md text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              title="Move up"
                              aria-label="Move basket up"
                            >
                              <ChevronUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleBasketMove(index, 1); }}
                              disabled={index === filteredBaskets.length - 1}
                              className="p-1 rounded-md text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              title="Move down"
                              aria-label="Move basket down"
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                        <EnableToggle
                          enabled={basket.status === BASKET_STATUS.ACTIVE}
                          loading={updatingBasket === basket.basket_id}
                          onChange={() => handleBasketStatusToggle(basket)}
                          size="sm"
                          showLabel={false}
                          stopPropagation
                          ariaLabel={basket.status === BASKET_STATUS.ACTIVE ? 'Disable basket' : 'Enable basket'}
                        />
                        <a
                          href="/help/basket-enable-disable.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-1 rounded-md text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                          title="Learn about basket enable/disable"
                        >
                          <HelpCircle className="h-3.5 w-3.5" />
                        </a>
                        <div className="flex items-center gap-0.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBasket(basket);
                              setShowEditBasketDialog(true);
                            }}
                            className={`p-1.5 rounded-md transition-colors ${wl ? 'text-zebu-600 hover:bg-zebu-100 dark:hover:bg-zebu-900/30' : 'text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30'}`}
                            title="Edit basket name and description"
                          >
                            <Edit3 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteBasket(basket);
                            }}
                            className="p-1.5 rounded-md text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                            title="Delete basket"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
          </>
          )}

          {/* Drag handle — desktop only, when panel is expanded */}
          {isDesktop && !panelCollapsed && (
            <div
              onMouseDown={startResize}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize basket panel"
              title="Drag to resize"
              className="hidden lg:flex absolute top-0 right-0 bottom-0 w-2 cursor-col-resize justify-center items-stretch group z-10"
            >
              <div className="w-px bg-transparent group-hover:bg-blue-500 group-active:bg-blue-600 transition-colors" />
            </div>
          )}
        </div>

        {/* Right Panel - Hidden on mobile when no basket selected */}
        <div className={`flex-1 min-w-0 bg-gray-50 dark:bg-gray-900 flex flex-col ${selectedBasket ? 'flex' : 'hidden lg:flex'}`}>

          {/* Right Panel Header */}
          <div className="px-4 lg:px-6 py-3 border-b border-gray-100 dark:border-gray-800">
            {/* Mobile back button */}
            {selectedBasket && (
              <button
                onClick={() => setSelectedBasket(null)}
                className="lg:hidden flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to baskets
              </button>
            )}

            {selectedBasket ? (
              /* Tabs directly — name + edit moved to the left basket list; count folded into the Strategies tab. */
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex space-x-1">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'details' ? activeTabCls : idleTabCls}`}
                  >
                    Strategies ({selectedBasket.strategies?.length || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab('allocation')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'allocation' ? activeTabCls : idleTabCls}`}
                  >
                    Allocation
                  </button>
                  <button
                    onClick={() => setActiveTab('live')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'live' ? activeTabCls : idleTabCls}`}
                  >
                    Live
                  </button>
                  <button
                    onClick={() => setActiveTab('performance')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'performance' ? activeTabCls : idleTabCls}`}
                  >
                    Performance
                  </button>
                </div>
                <Badge
                  variant={selectedBasket.status === 'ACTIVE' ? 'success' : selectedBasket.status === 'PAUSED' ? 'warning' : 'default'}
                  size="sm"
                >
                  {selectedBasket.status}
                </Badge>
              </div>
            ) : (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Select a Basket</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Choose a basket from the left panel to view details
                </p>
              </div>
            )}
          </div>

          {/* Right Panel Content - Mobile Responsive */}
          <div className="flex-1 lg:overflow-y-auto p-4 lg:p-6">
            {selectedBasket ? (
              <div>
                {/* Strategies Tab */}
                <div className={activeTab === 'details' ? 'block' : 'hidden'}>
                  {/* Strategies Section */}
                  <Card>
                    <CardHeader>
                      {/* Stack title row + actions row on mobile (5 buttons can't
                          fit one row at ~360 px); side-by-side from sm+ as before.
                          `flex-wrap` on the actions row is a safety net for very
                          narrow phones or future button additions. */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                        <div className="flex items-center gap-3">
                          <CardTitle>Strategies</CardTitle>
                          <Badge variant="info" size="sm">
                            {selectedBasket.strategies?.length || 0}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 self-end sm:self-auto">
                          <Button
                            onClick={() => setShowStrategyWizard(true)}
                            leftIcon={<Plus className="h-4 w-4" />}
                            size="sm"
                            className="whitespace-nowrap"
                          >
                            Add Strategy
                          </Button>

                          {/* Publish to Marketplace Button - Admin only.
                              Label hidden on <sm (icon-only on mobile to avoid
                              the cramped 5-button row); native long-press
                              tooltip via `title` keeps the action discoverable. */}
                          {isAdmin && selectedBasket && (
                            <Button
                              size="sm"
                              variant="outline"
                              leftIcon={<Globe className="h-4 w-4" />}
                              onClick={() => setPublishingBasket(selectedBasket)}
                              disabled={!!selectedBasket.is_published}
                              title={selectedBasket.is_published ? 'Already published to marketplace' : 'Publish to Marketplace'}
                            >
                              <span className="hidden sm:inline">
                                {selectedBasket.is_published ? 'Published' : 'Publish'}
                              </span>
                            </Button>
                          )}

                          {/* Export bundle - any user-owned basket with strategies */}
                          {selectedBasket && selectedBasket.strategies && selectedBasket.strategies.length > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              leftIcon={<Upload className="h-4 w-4" />}
                              onClick={() => setExportingBasket(selectedBasket)}
                              title="Export selected strategies as a JSON bundle"
                            >
                              <span className="hidden sm:inline">Export</span>
                            </Button>
                          )}

                          {/* Copy strategies to another basket (intra-account) */}
                          {selectedBasket && selectedBasket.strategies && selectedBasket.strategies.length > 0 && baskets.length > 1 && (
                            <Button
                              size="sm"
                              variant="outline"
                              leftIcon={<Copy className="h-4 w-4" />}
                              onClick={() => setCopyingFromBasket(selectedBasket)}
                              title="Copy selected strategies to another basket"
                            >
                              <span className="hidden sm:inline">Copy to…</span>
                            </Button>
                          )}

                          {/* Delete All Strategies - icon only */}
                          {selectedBasket && selectedBasket.strategies && selectedBasket.strategies.length > 0 && (
                            <Tooltip text={`Delete all ${selectedBasket.strategies.length} strategies`}>
                              <button
                                onClick={() => handleBulkDeleteStrategies(selectedBasket)}
                                className="p-2 rounded-lg text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {strategiesLoading ? (
                          <LoadingSpinner message="Loading strategies..." />
                        ) : selectedBasket.strategies && selectedBasket.strategies.length > 0 ? (
                          <StrategyRowView
                            strategies={selectedBasket.strategies}
                            basketId={selectedBasket.basket_id}
                            onAfterSave={async () => {
                              if (!selectedBasket) return;
                              try {
                                const strategies = await strategyService.getBasketStrategies(selectedBasket.basket_id);
                                setSelectedBasket(prev => (prev ? { ...prev, strategies } : null));
                                setBaskets(prev =>
                                  prev.map(b =>
                                    b.basket_id === selectedBasket.basket_id ? { ...b, strategies } : b,
                                  ),
                                );
                              } catch {
                                /* refresh failure is non-fatal — the save already succeeded */
                              }
                            }}
                            onEdit={handleEditStrategy}
                            onDelete={handleDeleteStrategy}
                            onCopy={handleCopyStrategy}
                            onStatusToggle={handleStatusToggle}
                            onEntrySelectionUpdate={handleEntrySelectionUpdate}
                            updatingStrategy={updatingStrategy}
                            onReorder={applyStrategyReorder}
                            reorderEnabled={sortBy === 'custom'}
                            isDesktop={isDesktop}
                          />
                        ) : (
                          <div className="text-center py-8">
                            <div className="text-sm font-medium text-gray-500 mb-4">Details</div>
                            <h3 className="text-lg font-medium mb-2">No Strategies Yet</h3>
                            <p className="text-gray-600 mb-4">Use the "Add Strategy" button above to start building this basket</p>
                          </div>
                        )}
                    </CardContent>
                  </Card>
                </div>

                {/* Broker Allocation Tab */}
                <div className={activeTab === 'allocation' ? 'block' : 'hidden'}>
                  <BasketAllocation
                    basket={{
                      ...selectedBasket,
                      strategies: selectedBasket.strategies || []
                    }}
                    onAllocationComplete={() => {
                      showSuccess('Broker allocation updated successfully!');
                      setActiveTab('details');
                    }}
                  />
                </div>

                {/* Live Tab */}
                <div className={activeTab === 'live' ? 'block' : 'hidden'}>
                  {activeTab === 'live' && (
                    <BasketScheduleView
                      key={selectedBasket.basket_id}
                      basketId={selectedBasket.basket_id}
                    />
                  )}
                </div>

                {/* Performance Tab — reuses the Analytics → Baskets section,
                    pre-scoped to the currently selected basket so the
                    breakdown table shows just this basket and its strategies. */}
                <div className={activeTab === 'performance' ? 'block' : 'hidden'}>
                  {activeTab === 'performance' && selectedBasket && (
                    <BasketPerformanceTab basketId={selectedBasket.basket_id} />
                  )}
                </div>
              </div>
            ) : (
              /* Empty State */
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-500 mb-6">No baskets</div>
                  <h3 className="text-xl font-semibold mb-8">Welcome to Basket Management</h3>
                  <Button onClick={() => setShowCreateDialog(true)} leftIcon={<Plus className="h-4 w-4" />}>
                    Create Your First Basket
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {showCreateDialog && (
        <CreateBasketDialog
          onClose={() => setShowCreateDialog(false)}
          onSubmit={handleCreateBasket}
        />
      )}

      {showEditBasketDialog && selectedBasket && (
        <EditBasketDialog
          basket={selectedBasket}
          onClose={() => setShowEditBasketDialog(false)}
          onSubmit={handleEditBasket}
        />
      )}

      {showStrategyWizard && selectedBasket && (
        <StrategyEditorModal
          basketId={selectedBasket.basket_id}
          onCancel={() => setShowStrategyWizard(false)}
          onDone={handleAddStrategy}
        />
      )}

      {showEditStrategyDialog && selectedBasket && editingStrategy && (
        <StrategyEditorModal
          basketId={selectedBasket.basket_id}
          editingStrategy={editingStrategy}
          onCancel={() => {
            setShowEditStrategyDialog(false);
            setEditingStrategy(null);
          }}
          onDone={handleUpdateStrategy}
        />
      )}

      {/* Delete Basket Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmDialog.isOpen}
        onClose={() => setDeleteConfirmDialog({ isOpen: false, basket: null })}
        onConfirm={confirmDeleteBasket}
        title="Delete Basket"
        message={`Are you sure you want to delete the basket "${deleteConfirmDialog.basket?.basket_name || 'this basket'}"? This action cannot be undone. All associated strategies and data will be permanently removed.`}
        confirmText="Delete Basket"
        cancelText="Keep Basket"
        variant="danger"
        icon={<Trash2 className="h-6 w-6 text-red-600" />}
      />

      {/* Delete Strategy Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteStrategyConfirmDialog.isOpen}
        onClose={() => setDeleteStrategyConfirmDialog({ isOpen: false, strategy: null })}
        onConfirm={confirmDeleteStrategy}
        title="Delete Strategy"
        message={`Are you sure you want to delete the strategy "${deleteStrategyConfirmDialog.strategy?.strategyName || 'this strategy'}"? This action cannot be undone. All strategy configurations and data will be permanently removed.`}
        confirmText="Delete Strategy"
        cancelText="Keep Strategy"
        variant="danger"
        icon={<Trash2 className="h-6 w-6 text-red-600" />}
      />

      {/* Bulk Delete Strategies Confirmation Dialog */}
      <BulkDeleteConfirmationDialog
        isOpen={bulkDeleteDialog.isOpen}
        onClose={() => setBulkDeleteDialog({ isOpen: false, basket: null, isDeleting: false })}
        onConfirm={confirmBulkDeleteStrategies}
        title="Delete All Strategies"
        itemCount={bulkDeleteDialog.basket?.strategies?.length || 0}
        itemType="strategies"
        isDeleting={bulkDeleteDialog.isDeleting}
        deletingMessage={`Deleting ${bulkDeleteDialog.basket?.strategies?.length || 0} strategies...`}
      />

      {/* Publish to Marketplace Dialog (Admin only) */}
      {publishingBasket && (
        <PublishBasketDialog
          basketId={publishingBasket.basket_id}
          basketName={publishingBasket.basket_name}
          onClose={() => setPublishingBasket(null)}
          onSuccess={() => {
            const publishedId = publishingBasket.basket_id;
            setPublishingBasket(null);
            setBaskets(prev => prev.map(b => b.basket_id === publishedId ? { ...b, is_published: true } : b));
            setSelectedBasket(prev => prev?.basket_id === publishedId ? { ...prev, is_published: true } : prev);
            showSuccess('Basket published to marketplace!');
          }}
        />
      )}

      {/* Export Strategies Dialog */}
      <ExportStrategiesDialog
        isOpen={!!exportingBasket}
        basketId={exportingBasket?.basket_id || ''}
        basketName={exportingBasket?.basket_name || ''}
        strategies={exportingBasket?.strategies || []}
        onClose={() => setExportingBasket(null)}
      />

      {/* Copy Strategies Dialog (intra-account, cross-basket) */}
      <CopyStrategiesDialog
        isOpen={!!copyingFromBasket}
        sourceBasketId={copyingFromBasket?.basket_id || ''}
        sourceBasketName={copyingFromBasket?.basket_name || ''}
        strategies={copyingFromBasket?.strategies || []}
        allBaskets={baskets}
        onClose={() => setCopyingFromBasket(null)}
        onCopied={(targetBasketId) => {
          // Drop cached strategies on the target so the next selection
          // refetches and picks up the new copies.
          setBaskets(prev =>
            prev.map(b => b.basket_id === targetBasketId ? { ...b, strategies: undefined } : b),
          );
          if (selectedBasket?.basket_id === targetBasketId) {
            setSelectedBasket(prev => prev ? { ...prev, strategies: undefined } : null);
          }
        }}
      />
    </>
  );
};

export default TabbedBasketManager;